import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { ElectionBoardRow } from './electionBoardUtils'

function useAnimatedNumber(target: number, duration = 900) {
  const [display, setDisplay] = useState(target)
  const prevRef = useRef(target)

  useEffect(() => {
    const start = prevRef.current
    const diff = target - start
    if (diff === 0) return

    const t0 = performance.now()
    let raf = 0

    const tick = (now: number) => {
      const progress = Math.min(1, (now - t0) / duration)
      const eased = 1 - (1 - progress) ** 3
      setDisplay(Math.round(start + diff * eased))
      if (progress < 1) raf = requestAnimationFrame(tick)
      else prevRef.current = target
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])

  return display
}

function AnimatedValue({ value }: { value: number }) {
  const animated = useAnimatedNumber(value)
  return <>{animated.toLocaleString('th-TH')}</>
}

type Props = {
  title: string
  subtitle?: string
  footerTitle?: string
  rows: ElectionBoardRow[]
  totalStake?: number
  totalBets?: number
  updatedAt?: string
  maxVisible?: number
  live?: boolean
  loading?: boolean
  emptyMessage?: string
}

function formatThaiDateTime(iso?: string) {
  if (!iso) return new Date().toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
  return new Date(iso).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
}

export default function ElectionResultsLiveBoard({
  title,
  subtitle,
  footerTitle,
  rows,
  totalStake = 0,
  totalBets = 0,
  updatedAt,
  maxVisible = 10,
  live = true,
  loading = false,
  emptyMessage = 'ยังไม่มีข้อมูลการวางเดิมพัน',
}: Props) {
  const visibleRows = useMemo(() => rows.slice(0, maxVisible), [rows, maxVisible])
  const leaderStake = visibleRows[0]?.totalStake || 1

  return (
    <div
      className="election-live-board mb-3"
      style={{
        background: 'linear-gradient(180deg, #f5f0e8 0%, #ece6dc 38%, #2b3038 72%, #1a1d22 100%)',
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
      }}
    >
      <div style={{ padding: '14px 16px 18px' }}>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <div style={{ fontSize: 11, color: '#666' }}>{formatThaiDateTime(updatedAt)}</div>
            {subtitle ? <div style={{ fontSize: 10, color: '#888' }}>{subtitle}</div> : null}
          </div>
          <div className="d-flex align-items-center gap-2">
            {live ? (
              <span
                className="election-live-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '4px 10px',
                  borderRadius: 999,
                  letterSpacing: '0.06em',
                }}
              >
                <span className="election-live-dot" />
                LIVE
              </span>
            ) : null}
            <div style={{ fontSize: 10, fontWeight: 700, color: '#666', textTransform: 'uppercase' }}>ยอดเดิมพัน · %</div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4 c_primary">กำลังโหลด...</div>
        ) : visibleRows.length === 0 ? (
          <div className="text-center py-4 c_primary">{emptyMessage}</div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {visibleRows.map((row) => {
              const barWidth = Math.max(18, Math.round((row.totalStake / leaderStake) * 100))
              return (
                <div key={row.id} className="d-flex align-items-stretch election-row-enter">
                  <div className="d-flex align-items-center justify-content-center" style={{ width: 32, flexShrink: 0 }}>
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.85)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#444',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                      }}
                    >
                      {row.rank}
                    </div>
                  </div>

                  <div className="d-flex align-items-stretch flex-grow-1 min-w-0 position-relative">
                    <div
                      className="election-bar-fill"
                      style={{
                        backgroundColor: row.barColor,
                        width: `${barWidth}%`,
                        minWidth: '42%',
                        minHeight: 52,
                        borderRadius: '16px 0 0 16px',
                        padding: '8px 12px',
                        color: '#fff',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        transition: 'width 0.85s cubic-bezier(0.22, 1, 0.36, 1)',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(90deg, rgba(255,255,255,0.12), transparent)',
                        }}
                      />
                      <div style={{ position: 'relative', paddingRight: 36 }}>
                        <div className="fw-bold text-truncate" style={{ fontSize: 14 }}>
                          {row.badgeLabel ? `${row.badgeLabel} ` : ''}{row.label}
                        </div>
                        <div className="text-truncate" style={{ fontSize: 11, opacity: 0.92 }}>
                          <AnimatedValue value={row.totalStake} /> บาท · {row.totalBets.toLocaleString('th-TH')} ใบ
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        marginLeft: -20,
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: '50%',
                          border: '4px solid #fff',
                          background: '#fff',
                          overflow: 'hidden',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          color: '#444',
                        }}
                      >
                        {row.imageUrl ? (
                          <img src={row.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          row.rank
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        flex: 1,
                        minWidth: 88,
                        background: '#fff',
                        borderRadius: '0 16px 16px 0',
                        padding: '8px 12px',
                        textAlign: 'right',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <div style={{ fontSize: 28, fontWeight: 900, lineHeight: 1, color: '#111' }}>
                        {row.stakePercent.toFixed(1)}
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#888' }}>%</div>
                      <div style={{ fontSize: 10, color: '#aaa' }}>{row.totalBets.toLocaleString('th-TH')} ใบ</div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div className="text-center text-white mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
          <div style={{ fontSize: 20, fontWeight: 900 }}>{footerTitle || title}</div>
          <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>
            ยอดรวม <AnimatedValue value={totalStake} /> บาท · {totalBets.toLocaleString('th-TH')} ใบ
          </div>
        </div>
      </div>

      <style>{`
        .election-live-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #fff;
          position: relative;
        }
        .election-live-dot::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: #fff;
          animation: election-ping 1.2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        @keyframes election-ping {
          0% { transform: scale(1); opacity: 0.8; }
          75%, 100% { transform: scale(2.2); opacity: 0; }
        }
        .election-row-enter {
          animation: election-row-in 0.55s cubic-bezier(0.22, 1, 0.36, 1);
        }
        @keyframes election-row-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}
